// Equipment (§31 A0): the half of an artifact a Monarch carries.
//
// **An artifact is two things — a structure on the board and gear on a
// Monarch — and the function lives while both stand.** `hasPower` asks
// `poweredCount`: gear of the power's kind, borne by the side, whose anchor
// structure still stands under the bearer **or under the side that placed it**.
// The second clause is the theft (Q11): a Monarch falls, its gear drops on the
// square, any Monarch standing there picks it up, and the thief has the
// function while the maker's structure stands. **Destroying your own
// structure kills the stolen copy** — gear whose anchor is gone is pruned.
//
// **One diff, every door** (`syncGear`, run by `applyCommand` after ids are
// stamped): held gear follows its bearer by `pieceId`, so no door that moves,
// shoves or kills a body is taught about it — `stampBodies`' and
// `payForMonarchs`' shape. It also **issues** gear: an artifact structure with
// no gear anchored to it hands its gear to its owner's nearest Monarch, so a
// grant-token, a scenario and a level all arm the same way. A structure whose
// owner has no Monarch issues later, when one stands.
//
// IndustryState, not Position: nothing in movegen or legality reads it.

import { fileOf, pieceColor, rankOf, type Side } from "../board";
import {
  ALTAR,
  ARTISAN_PILLAR,
  ENDURANCE_MONUMENT,
  FIEND_FOUNTAIN,
  FUSION_CHAMBER,
  HOUSE_OF_CARDS,
  LEGION_TOWER,
  PROPAGANDA_TOWER,
  SHIELD_GENERATOR,
  type Gear,
  type IndustryState,
  isArtifactKind,
  KEYSTONE,
  RELIC,
  type StructureKind,
  structureAt,
  WELL
} from "../industry/state";
import { needsPieceIds, pieceAt, pieceIdAt, type Position, royalsOf, stampPieceIds } from "../position";
import type { GameState } from "../commands";
import { cloneState, IllegalCommandError, requireBuilder } from "../reduce";

/** The doc's name for each artifact's gear. */
const GEAR_NAME: Partial<Record<StructureKind, string>> = {
  [ALTAR]: "Sacrificial Kris",
  [RELIC]: "Titan Pendant",
  [KEYSTONE]: "Turret Manager Badge",
  [WELL]: "Oil-Stone Scepter",
  [FUSION_CHAMBER]: "Signet of Union",
  [SHIELD_GENERATOR]: "Shield Specialist Badge",
  [ARTISAN_PILLAR]: "Mark of Excellence",
  [PROPAGANDA_TOWER]: "Efficiency Mandate",
  [HOUSE_OF_CARDS]: "Unrelenting Knuckles of Brass",
  [ENDURANCE_MONUMENT]: "Shield of Defiance",
  [FIEND_FOUNTAIN]: "Fiend's Tail",
  [LEGION_TOWER]: "Legion Organizer"
};

export const gearName = (kind: StructureKind): string => GEAR_NAME[kind] ?? "Equipment";

// The pure queries live in `industry/gear.ts` (the power network asks them).
export { gearOf, gearPowered, poweredCount } from "../industry/gear";
import { gearOf, gearPowered } from "../industry/gear";

/** Gear borne by the body on `sq`, in list order — the order the inspector lists and `give-equipment` indexes. */
export function gearBorneAt(position: Position, industry: IndustryState, sq: number): Gear[] {
  const id = pieceIdAt(position, sq);
  if (id <= 0) return [];
  return gearOf(industry).filter((gear) => gear.holder === id);
}

/** Gear lying on `sq`. */
export const gearLyingAt = (industry: IndustryState, sq: number): Gear[] =>
  gearOf(industry).filter((gear) => gear.holder === 0 && gear.at === sq);

/** Every Monarch's square, by side, ascending — `-1` holes dropped. */
function monarchSquares(position: Position): Map<number, Side> {
  const out = new Map<number, Side>();
  for (let side = 0; side < position.sides; side++) {
    for (const sq of royalsOf(position, side as Side)) if (sq >= 0) out.set(sq, side as Side);
  }
  return out;
}

const chebyshev = (a: number, b: number): number =>
  Math.max(Math.abs(fileOf(a) - fileOf(b)), Math.abs(rankOf(a) - rankOf(b)));

/**
 * `side`'s Monarch nearest `sq` (Chebyshev, ties by square), or -1 — the one
 * an artifact standing there arms, and so the one whose inventory pays for it
 * first (§31 A3–A5).
 */
export function nearestMonarch(position: Position, side: Side, sq: number): number {
  let best = -1;
  for (const at of royalsOf(position, side)) {
    if (at < 0) continue;
    if (best < 0 || chebyshev(at, sq) < chebyshev(best, sq) || (chebyshev(at, sq) === chebyshev(best, sq) && at < best)) {
      best = at;
    }
  }
  return best;
}

/**
 * The square of a `side` Monarch bearing a powered piece of `kind` gear, in
 * list order — the Kris bearers (§31 A3).
 */
export function poweredBearers(industry: IndustryState, side: number, kind: StructureKind): number[] {
  const out: number[] = [];
  for (const gear of gearOf(industry)) {
    if (gear.kind === kind && gear.side === side && gear.holder !== 0 && gearPowered(industry, gear)) out.push(gear.at);
  }
  return out;
}

/** Is there anything for `syncGear` to do? Cheap: no gear and no artifact standing is the common board. */
function gearInPlay(industry: IndustryState): boolean {
  if (gearOf(industry).length > 0) return true;
  for (const structure of industry.structures.values()) if (isArtifactKind(structure.kind)) return true;
  return false;
}

const sameGear = (a: readonly Gear[], b: readonly Gear[]): boolean =>
  a.length === b.length &&
  a.every(
    (g, i) =>
      g.kind === b[i].kind &&
      g.origin === b[i].origin &&
      g.anchor === b[i].anchor &&
      g.holder === b[i].holder &&
      g.side === b[i].side &&
      g.at === b[i].at
  );

/**
 * Bring the gear up to date with the board after a command: prune, follow,
 * drop, pick up, issue — in that order. Returns `next` itself when nothing
 * changed, else a copy with a fresh `industry.gear`.
 */
export function syncGear(next: GameState): GameState {
  const { position, industry } = next;
  if (!gearInPlay(industry)) return next;
  const where = new Map<number, number>();
  for (const [sq, id] of position.pieceId) where.set(id, sq);
  const monarchs = monarchSquares(position);

  const out: Gear[] = [];
  for (const gear of gearOf(industry)) {
    // Prune: the structure it pairs with is gone (or is something else now).
    const anchor = structureAt(industry, gear.anchor);
    if (!anchor || anchor.kind !== gear.kind) continue;
    if (gear.holder === 0) {
      out.push(gear);
      continue;
    }
    const sq = where.get(gear.holder);
    // Its bearer is gone: it drops where the bearer last stood (Q11) — on a
    // capture or a ring out, the square the taker now holds.
    if (sq === undefined) out.push({ ...gear, holder: 0, side: -1 });
    else out.push({ ...gear, at: sq, side: pieceColor(pieceAt(position, sq)) });
  }

  // Pick up: any Monarch standing on lying gear takes it — the thief included.
  for (let i = 0; i < out.length; i++) {
    const gear = out[i];
    if (gear.holder !== 0) continue;
    const side = monarchs.get(gear.at);
    const id = pieceIdAt(position, gear.at);
    if (side === undefined || id <= 0) continue;
    out[i] = { ...gear, holder: id, side };
  }

  // Issue: an artifact standing with no gear anchored to it arms its owner's
  // nearest Monarch, ties by square.
  const anchored = new Set(out.map((gear) => gear.anchor));
  const artifacts = [...industry.structures.entries()]
    .filter(([sq, s]) => isArtifactKind(s.kind) && !anchored.has(sq))
    .sort((a, b) => a[0] - b[0]);
  for (const [sq, structure] of artifacts) {
    const best = nearestMonarch(position, structure.owner as Side, sq);
    if (best < 0 || pieceIdAt(position, best) <= 0) continue;
    out.push({
      kind: structure.kind,
      origin: structure.owner,
      anchor: sq,
      holder: pieceIdAt(position, best),
      side: structure.owner,
      at: best
    });
  }

  const before = gearOf(industry);
  if (sameGear(before, out)) return next;
  const { gear: _old, ...rest } = industry;
  return { ...next, industry: out.length > 0 ? { ...rest, gear: out } : rest };
}

/**
 * `give-equipment`: hand one piece of gear from a Monarch to a friendly
 * Monarch beside it (Chebyshev 1). Free, turn-bound, a player's act.
 */
export function applyGiveEquipment(
  state: GameState,
  command: { owner: Side; from: number; to: number; item: number }
): GameState {
  const owner = requireBuilder(state, command.owner);
  const { position, industry } = state;
  const monarchs = monarchSquares(position);
  if (monarchs.get(command.from) !== owner) throw new IllegalCommandError("no Monarch of yours to give from");
  if (monarchs.get(command.to) !== owner) throw new IllegalCommandError("equipment goes to a Monarch of yours");
  if (command.from === command.to) throw new IllegalCommandError("that Monarch already bears it");
  if (chebyshev(command.from, command.to) !== 1) throw new IllegalCommandError("the two Monarchs must stand side by side");
  const borne = gearBorneAt(position, industry, command.from);
  const item = borne[command.item];
  if (!Number.isInteger(command.item) || !item) throw new IllegalCommandError("that Monarch bears no such equipment");
  const to = pieceIdAt(position, command.to);
  const next = cloneState(state);
  next.industry.gear = gearOf(industry).map((g) => (g === item ? { ...g, holder: to, at: command.to } : g));
  return next;
}

/**
 * Issue and follow gear on a state written outside a command — a test's
 * fixture, a snapshot from before A0. Mutates `state.industry`.
 */
export function armGear(state: GameState): void {
  if (needsPieceIds(state.position)) stampPieceIds(state.position);
  const synced = syncGear(state);
  if (synced.industry.gear) state.industry.gear = synced.industry.gear;
  else delete state.industry.gear;
}
